import { Badge, Button, DatePicker, Divider, Form, Input, Select, Space } from 'antd'
import type { ValidateErrorEntity } from 'rc-field-form/lib/interface'

import React, { useEffect } from 'react'
import type { Moment } from 'moment'

//types
import type { ContentStatus, ContentTag, SupportedContentFormats } from '@a4b/api'

//constants
import {
  CONTENT_FORMATS,
  CONTENT_STATUS,
  SUPPORTED_LANGUAGES_LIST,
  getLanguageNameFromValue,
} from '../../../constants'

export type ContentFilterFormFields = {
  status?: ContentStatus[]
  name?: string
  formats?: SupportedContentFormats[]
  email?: string
  publishedFrom?: string
  publishedTo?: string
}
export interface Props {
  tags: ContentTag[]
  filter?: ContentFilterFormFields
  onApplyFilterClicked: (filter: ContentFilterFormFields) => void,
  onTagSearch: (value: string, offset: number, language?: string) => void
}

const { Option } = Select
const { useForm } = Form
const { RangePicker } = DatePicker

const TAG_LANGUAGE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Hindi-English', value: 'hi_en' },
  ...SUPPORTED_LANGUAGES_LIST.map((lang) => ({
    label: lang.alt_name,
    value: lang.value,
  })),
]

export const LibraryContentFilter: React.FC<Props> = ({
  tags,
  filter,
  onApplyFilterClicked,
  onTagSearch,
}) => {
  const [offset, setOffset] = React.useState(0);
  const [searchText, setSearchText] = React.useState('');
  const [tagLanguage, setTagLanguage] = React.useState<string>('');
  const [form] = useForm<ContentFilterFormFields & { publishedRange?: [Moment | null, Moment | null] }>()

  useEffect(() => {
    if (filter) {
      form.setFieldsValue({
        ...filter,
        publishedRange: undefined,
      })
    }
  }, [filter])

  const handleApplyFilter = (values: ContentFilterFormFields & { publishedRange?: [Moment | null, Moment | null] }) => {
    setOffset(0);
    const { publishedRange, ...rest } = values
    const filterValues: ContentFilterFormFields = {
      ...rest,
      email: rest.email?.trim() || undefined,
      publishedFrom: publishedRange?.[0] ? publishedRange[0].toISOString() : undefined,
      publishedTo: publishedRange?.[1] ? publishedRange[1].toISOString() : undefined,
    }
    onApplyFilterClicked(filterValues)
  }

  const handleApplyFilterFailed = (
    errorInfo: ValidateErrorEntity<ContentFilterFormFields>,
  ) => {
    console.log({ errorInfo })
  }

  const handleSearch = (key: string, _offset?: number) => {
    onTagSearch(key, _offset !== undefined ? _offset : offset, tagLanguage || undefined)
  }

  const handleTagLanguageChange = (value: string) => {
    setTagLanguage(value)
    onTagSearch(searchText, 0, value || undefined)
    setOffset(0)
  }

  const handleClearFilters = () => {
    form.resetFields()
    setTagLanguage('')
    setSearchText('')
    setOffset(0)
    onTagSearch('', 0, undefined)
    onApplyFilterClicked({})
  }

  return (
    <Form
      form={form}
      layout={'inline'}
      onFinish={handleApplyFilter}
      onFinishFailed={handleApplyFilterFailed}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}
    >
      <Form.Item label='Status' name='status'>
        <Select
          mode='multiple'
          style={{ minWidth: '200px', maxWidth: '500px' }}
          placeholder='Content Status'
        >
          {CONTENT_STATUS.map((status, index) => (
            <Option key={index} value={status}>
              {status}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label='Format' name='formats'>
        <Select
          mode='multiple'
          style={{ minWidth: '200px' }}
          placeholder='Content Format'
        >
          {CONTENT_FORMATS.map((format, index) => (
            <Option key={index} value={format}>
              {format.toUpperCase()}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label='Name' name='name'>
        <Input placeholder='Content name' />
      </Form.Item>
      <Form.Item label='Author Email' name='email'>
        <Input placeholder='author@example.com' style={{ minWidth: '200px' }} />
      </Form.Item>
      <Form.Item label='Published Date' name='publishedRange'>
        <RangePicker
          showTime
          format='YYYY-MM-DD HH:mm:ss'
        />
      </Form.Item>
      <Space>
        <Select
          value={tagLanguage}
          onChange={handleTagLanguageChange}
          style={{ minWidth: '130px' }}
          placeholder='Tag language'
        >
          {TAG_LANGUAGE_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
        <Form.Item
          label='Tags'
          name='tags'
          style={{ marginBottom: 0 }}
        >
          <Select
            mode='multiple'
            showSearch
            placeholder={'Search tags'}
            filterOption={false}
            searchValue={searchText}
            onSearch={(text) => {
              handleSearch(text, 0)
              setSearchText(text)
              setOffset(0);
            }}
            style={{ minWidth: '400px', width: '100%' }}
            dropdownRender={(menu) => {
              return <div>
                {menu}
                <Divider></Divider>
                <Button type="text"
                  onClick={() => {
                    setOffset(Math.max(offset - 1, 0));
                    handleSearch(searchText, Math.max(offset - 1, 0));
                  }}
                  disabled={offset === 0}
                >
                  Previous Page {offset}
                </Button>
                (Page {offset + 1})
                <Button type="text" value={1 + 1}
                  onClick={() => {
                    setOffset(offset + 1);
                    handleSearch(searchText, offset + 1)
                  }}
                  disabled={tags.length < 30}
                >
                  Next Page {offset + 2}
                </Button>
              </div>
            }}
          >
            {tags.map((tag, index) => (
              <Option key={index} value={tag.id} >
                {
                  tag.active
                    ? <Badge status="success" text={`${tag.name} (${getLanguageNameFromValue(tag.language)})`} />
                    : <Badge status="error" text={`${tag.name} (${getLanguageNameFromValue(tag.language)})`} />
                }
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Space>
      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Apply
        </Button>
      </Form.Item>
      <Form.Item>
        <Button onClick={handleClearFilters}>
          Clear Filters
        </Button>
      </Form.Item>
    </Form>
  )
}

export default LibraryContentFilter
